import { useEffect, useState } from "react";
import axiosInstance from "config/axiosInstance";
import { Box, TextField, IconButton } from "@mui/material";
import Edit from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiAvatar from "components/VuiAvatar";
import { useConfirm } from "hooks/useConfirm";
import { useAppAlert } from "hooks/useAppAlert";
import { normalizePhone, isValidPeruPhone } from "utils/phone";

import Card from "@mui/material/Card";
import Table from "examples/Tables/Table";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

export default function SecondaryUsers() {
  const { confirm, ConfirmModal } = useConfirm();
  const { showAlert, AlertBanner } = useAppAlert();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", correo: "", celular: "", password: "" });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/secondary-users");
      if (res.data.success) setUsers(res.data.usuarios || []);
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Error cargando usuarios secundarios";
      showAlert(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ nombre: "", correo: "", celular: "", password: "" });
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ nombre: u.nombre || "", correo: u.correo || "", celular: u.celular || "", password: "" });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    try {
      if (!form.nombre || !form.correo) {
        showAlert("Nombre y correo son obligatorios", "warning");
        return;
      }
      if (!isValidPeruPhone(form.celular)) {
        showAlert("El celular debe ser un número válido de Perú (ej. 906210316 o 51906210316)", "warning");
        return;
      }

      const celularNormalizado = normalizePhone(form.celular);

      if (editing) {
        const payload = {
          nombre: form.nombre,
          correo: form.correo,
          celular: celularNormalizado,
          activo: editing.activo,
        };
        if (form.password && form.password.length > 0) payload.password = form.password;

        await axiosInstance.put(`/secondary-users/${editing.id}`, payload);
        showAlert("Usuario actualizado exitosamente", "success");
      } else {
        if (!form.password) {
          showAlert("La contraseña es obligatoria para crear un usuario", "warning");
          return;
        }
        await axiosInstance.post("/secondary-users", {
          ...form,
          celular: celularNormalizado,
        });
        showAlert("Usuario creado exitosamente", "success");
      }

      handleClose();
      fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Error guardando usuario";
      showAlert(errorMessage, "error");
    }
  };

  const handleDelete = async (u) => {
    const ok = await confirm(`¿Eliminar usuario ${u.nombre}?`, "Eliminar usuario");
    if (!ok) return;
    try {
      await axiosInstance.delete(`/secondary-users/${u.id}`);
      showAlert("Usuario eliminado exitosamente", "success");
      fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Error eliminando usuario";
      showAlert(errorMessage, "error");
    }
  };

  const columns = [
    { name: "nombre", align: "left" },
    { name: "correo", align: "left" },
    { name: "celular", align: "left" },
    { name: "acciones", align: "center", label: "Acciones" },
  ];

  const rows = users.map((u) => ({
    nombre: (
      <VuiBox display="flex" alignItems="center">
        <VuiAvatar src={null} name={u.nombre} size="sm" />
        <VuiTypography pl="12px" color="white" fontWeight="medium">
          {u.nombre}
        </VuiTypography>
      </VuiBox>
    ),
    correo: (
      <VuiTypography variant="button" color="white" fontWeight="regular">
        {u.correo}
      </VuiTypography>
    ),
    celular: (
      <VuiTypography variant="button" color="white" fontWeight="regular">
        {u.celular}
      </VuiTypography>
    ),
    acciones: (
      <Box display="flex" justifyContent="center">
        <IconButton size="small" onClick={() => openEdit(u)}>
          <Edit sx={{ color: "#9CA3FF" }} />
        </IconButton>
        <IconButton size="small" onClick={() => handleDelete(u)}>
          <Delete sx={{ color: "#FF6B6B" }} />
        </IconButton>
      </Box>
    ),
  }));

  return (
    <Card>
      <ConfirmModal />
      <VuiBox m={2}>
        <AlertBanner />
      </VuiBox>
      
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <VuiTypography variant="lg" color="white">
          Usuarios secundarios
        </VuiTypography>
        <VuiButton size="small" color="info" onClick={openCreate}>
          Crear usuario
        </VuiButton>
      </VuiBox>

      <VuiBox p={2}>
        <Table columns={columns} rows={rows} />
      </VuiBox>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }
        }}
      >
        <Card sx={{ overflow: 'visible' }}>
          <VuiBox sx={{ 
            borderBottom: '1px solid rgba(255, 255, 255, 0.125)',
            p: 3
          }}>
            <VuiTypography color="white" variant="h5" fontWeight="bold">
              {editing ? "✏️ Editar usuario secundario" : "➕ Crear usuario secundario"}
            </VuiTypography>
          </VuiBox>
          
          <VuiBox sx={{ p: 3 }}>
            <VuiBox sx={{ display: "grid", gap: 3 }}>
              <VuiBox>
                <VuiTypography component="label" variant="caption" color="white" fontWeight="bold" mb={1}>
                  Nombre completo *
                </VuiTypography>
                <VuiInput
                  placeholder="Ej: Juan Pérez"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.1) !important',
                    border: '1px solid rgba(226, 232, 240, 0.3)',
                    borderRadius: '15px',
                    color: 'white !important',
                    '& input': { 
                      color: 'white !important',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.4)',
                      }
                    },
                    '&:hover': {
                      border: '1px solid rgba(226, 232, 240, 0.5)',
                    },
                    '&:focus-within': {
                      border: '1px solid #0075FF',
                      boxShadow: '0 0 0 1px #0075FF',
                    }
                  }}
                />
              </VuiBox>

              <VuiBox>
                <VuiTypography component="label" variant="caption" color="white" fontWeight="bold" mb={1}>
                  Correo electrónico *
                </VuiTypography>
                <VuiInput
                  type="email"
                  placeholder="ejemplo@correo.com"
                  value={form.correo}
                  onChange={(e) => setForm({ ...form, correo: e.target.value })}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.1) !important',
                    border: '1px solid rgba(226, 232, 240, 0.3)',
                    borderRadius: '15px',
                    color: 'white !important',
                    '& input': { 
                      color: 'white !important',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.4)',
                      }
                    },
                    '&:hover': {
                      border: '1px solid rgba(226, 232, 240, 0.5)',
                    },
                    '&:focus-within': {
                      border: '1px solid #0075FF',
                      boxShadow: '0 0 0 1px #0075FF',
                    }
                  }}
                />
              </VuiBox>

              <VuiBox>
                <VuiTypography component="label" variant="caption" color="white" fontWeight="bold" mb={1}>
                  Número de celular *
                </VuiTypography>
                <VuiInput
                  placeholder="999999999"
                  value={form.celular}
                  onChange={(e) => {
                    setForm({ ...form, celular: e.target.value });
                  }}
                  inputProps={{ inputMode: "tel" }}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.1) !important',
                    border: '1px solid rgba(226, 232, 240, 0.3)',
                    borderRadius: '15px',
                    color: 'white !important',
                    '& input': { 
                      color: 'white !important',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.4)',
                      }
                    },
                    '&:hover': {
                      border: '1px solid rgba(226, 232, 240, 0.5)',
                    },
                    '&:focus-within': {
                      border: '1px solid #0075FF',
                      boxShadow: '0 0 0 1px #0075FF',
                    }
                  }}
                />
                {form.celular && !isValidPeruPhone(form.celular) && (
                  <VuiTypography variant="caption" color="warning" mt={0.5}>
                    ⚠️ Celular inválido. Ej: 906210316 o +51 906 210 316
                  </VuiTypography>
                )}
                {!form.celular && (
                  <VuiTypography variant="caption" color="text" mt={0.5} sx={{ opacity: 0.6 }}>
                    📱 Acepta 906210316, +51906210316, etc.
                  </VuiTypography>
                )}
              </VuiBox>

              <VuiBox>
                <VuiTypography component="label" variant="caption" color="white" fontWeight="bold" mb={1}>
                  {editing ? "Nueva contraseña (opcional)" : "Contraseña *"}
                </VuiTypography>
                <VuiInput
                  type="password"
                  placeholder={editing ? "Dejar vacío para mantener actual" : "Mín. 8 caracteres, letra y número"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.1) !important',
                    border: '1px solid rgba(226, 232, 240, 0.3)',
                    borderRadius: '15px',
                    color: 'white !important',
                    '& input': { 
                      color: 'white !important',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.4)',
                      }
                    },
                    '&:hover': {
                      border: '1px solid rgba(226, 232, 240, 0.5)',
                    },
                    '&:focus-within': {
                      border: '1px solid #0075FF',
                      boxShadow: '0 0 0 1px #0075FF',
                    }
                  }}
                />
                <VuiTypography variant="caption" color="text" mt={0.5} sx={{ opacity: 0.6 }}>
                  {editing ? "🔒 Dejar en blanco para mantener la contraseña actual" : "🔑 Mín. 8 caracteres, con letra y número"}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          </VuiBox>

          <VuiBox sx={{ 
            borderTop: '1px solid rgba(255, 255, 255, 0.125)',
            p: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2
          }}>
            <VuiButton 
              color="error" 
              onClick={handleClose}
              sx={{ 
                minWidth: '120px',
                background: 'linear-gradient(310deg, #FF6B6B, #D63939)',
                '&:hover': {
                  background: 'linear-gradient(310deg, #FF5252, #C92A2A)',
                }
              }}
            >
              Cancelar
            </VuiButton>
            <VuiButton 
              color="info" 
              onClick={handleSubmit}
              sx={{ minWidth: '120px' }}
            >
              {editing ? "💾 Actualizar" : "✅ Crear"}
            </VuiButton>
          </VuiBox>
        </Card>
      </Dialog>
    </Card>
  );
}
